import "@configura/babylon-view-react/dist/css/babylon-view-react.css";
import "@configura/debug-react/dist/css/debug.css";
import {
	CatalogueAPI,
	decodeCatalogueParams,
	DtoCatalogueParamsWithCidAndLang,
} from "@configura/web-api";
import { ErrorContext, OverlayLoading } from "@configura/web-ui";
import "@configura/web-ui/dist/css/web-ui.css";
import { toError } from "@configura/web-utilities";
import "pepjs";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, RouteComponentProps, Switch } from "react-router-dom";
import { DebugWrapper } from "./dev/DebugContext";
import { InspectorWrapper } from "./dev/InspectorContext";
import { TextualConfigurationWrapper } from "./dev/TextualConfigurationContext";
import ErrorBoundary from "./ErrorBoundary";
import { Home } from "./Home";
import styles from "./index.module.css";
import { NotFound404 } from "./NotFound404";
import { useAuth } from "./useAuth";

const CatalogueRoutes = React.lazy(() => import("./CatalogueRoutes"));

// The root of product browsing. In the example app browsing starts from the very root of the URL
const browsingRootUrl = "";

const api = new CatalogueAPI();
export const APIContext = React.createContext<CatalogueAPI>(api);

export const App: React.FC = () => {
	const [error, setError] = useState<Error>();

	const setUnknownError = useCallback((err: unknown) => setError(toError(err)), []);

	const auth = useAuth(setError);
	const cataloguePermissions = (auth && auth.apiSession.permissions) || [];

	useEffect(() => {
		api.auth = auth;
	}, [auth]);

	useEffect(() => {
		if (error) {
			console.log(error);
		}
	}, [error]);

	return (
		<React.StrictMode>
			<ErrorContext.Provider value={setUnknownError}>
				<ErrorBoundary>
					<APIContext.Provider value={api}>
						{error === undefined ? null : (
							<div className={styles.errorContainer}>
								<div className={styles.errorMessage}>{error.message}</div>
								<div>
									<button onClick={() => setError(undefined)}>Close</button>
								</div>
							</div>
						)}
						<InspectorWrapper>
							<DebugWrapper>
								<TextualConfigurationWrapper>
									<Router>
										<div className={styles.homeContent}>
											{auth === undefined ? null : (
												<Switch>
													<Route exact path="/">
														<Home
															cataloguePermissions={
																cataloguePermissions
															}
															browsingRootUrl={browsingRootUrl}
														/>
													</Route>
													<Route path="/:cid/:lang/:enterprise/:prdCat/:prdCatVersion/:vendor/:priceList">
														{(
															props: RouteComponentProps<
																Omit<
																	DtoCatalogueParamsWithCidAndLang,
																	"cid"
																> & {
																	cid: string; // Omitted and included to change type from number to string
																}
															>
														) => {
															const matchParams = props.match.params;

															const catParams: DtoCatalogueParamsWithCidAndLang =
																{
																	...decodeCatalogueParams(
																		matchParams
																	),
																	cid: parseInt(matchParams.cid),
																};

															return (
																<Suspense
																	fallback={
																		<OverlayLoading
																			fullWindow={true}
																			className={
																				styles.fontSize10
																			}
																		/>
																	}
																>
																	<CatalogueRoutes
																		{...props}
																		catParams={catParams}
																		browsingRootUrl={
																			browsingRootUrl
																		}
																	/>
																</Suspense>
															);
														}}
													</Route>

													<Route component={NotFound404} />
												</Switch>
											)}
										</div>
									</Router>
								</TextualConfigurationWrapper>
							</DebugWrapper>
						</InspectorWrapper>
					</APIContext.Provider>
				</ErrorBoundary>
			</ErrorContext.Provider>
		</React.StrictMode>
	);
};
